// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import type { AcViewType } from "~/ActivityList/preset/const/AcViewType";
import { TaskType } from "~/common/enums";
import { ApproverStatus } from "~/const/approval";
import { client } from "~/EasyProjects/Members/MembersModelsMapper";
import { HoursState } from "~/sharedComponents/time/hours/TimeInput";
import { RecordConversion, RecordField } from "./forms";

export type Entities<T> = Dictionary<T>;

export interface Project {
    ProjectId: number;
    Name: string;
    CanCreateTimeEntriesForOther?: boolean;
    CanCreateTimeEntries?: boolean;
    CanEditAllTimeEntries?: boolean;
    PortfolioId?: number;
    IsDraft?: boolean;
    IsTemplate?: boolean;
    SpaceId?: number;
}
export type Projects = Entities<Project>;

export interface Task {
    TaskId: number;
    Name?: string;
    Project?: number;
    IsBillable?: boolean;
    Parent?: number;
    HasParent?: boolean;
    TaskHierarchyLevel?: number;
    TaskType?: TaskType;
    IsMilestone?: boolean;
    IsClosed?: boolean;
    TreatAsCompleted?: boolean;
}
export type Tasks = Entities<Task>;

export interface ParentTask {
    TaskID: number;
    Name: string;
    WBSNumber?: number;
    TaskHierarchyLevel?: number;
}

export interface TimeLog {
    TimelogId: number;
    EntryDate?: Date;
    Duration?: number;
    Task?: number;
    Description?: string;
    Billable?: boolean;
    Billed?: boolean;
    CanDelete?: boolean;
    Approved?: boolean;
    CanEdit?: boolean;
    RowNumber?: number;
    CustomFields?: CustomFields;
    UserId?: number;
    TaskProgress?: number;
    UpdatePHL?: HoursState;
    [key: string]: any;

    IsSavingInProgress?: boolean;
}

export interface Form {
    FormID: number;
    Status: "Publish" | "Draft" | "Archive";
    Title: string;
    Description: string;
    DateCreated: Date;
    DateEdited: Date;
    ItemsCount: number;
    UserName: string;
    UserID: number;
    EditedByName: string;
    EditedByID: number;
    CanEdit: boolean;
    CanDelete: boolean;
    CanAddRecords: boolean;
    CanViewRecords: boolean;
    CanManageRecords: boolean;
    CanReceiveNotifications: boolean;
    IsAutoConvertEnabled: boolean;
    CanConvertProjectRecords: boolean;
    CanConvertActivityRecords: boolean;
}
export enum FormFieldType {
    Text = "Text",
    TextArea = "TextArea",
    FileUpload = "FileUpload",
    Heading = "Heading",
    Paragraph = "Paragraph",
    Separator = "Separator",
    Dropdown = "Dropdown",
    RadioList = "RadioList",
    CheckList = "CheckList",
    Date = "Date",
}

export interface FormRecord {
    RecordID: number;
    FormID: number;
    CanEdit: boolean;
    Email: string;
    Keywords: string;
    UserName: string;
    Time: Date;
    Title: string;
    CreatorUserId: number;
    IsUnread: boolean;
    IsConverted: boolean;
    AttachmentNames: string[];
    Items: RecordField[];
    Conversions: RecordConversion[];
    HasValues: boolean;
    IsOutdatedDetailsData?: boolean;
}

export interface SystemListEntity {
    Id: number;
    Name: string;
    Order?: number;
}

export interface Category extends SystemListEntity {
    CanDelete: boolean;
    IsCreating?: boolean;
}
export interface Priority extends SystemListEntity {
    PriorityOrder: number;
    IsUsedByTasks: boolean;
    IsUsedByProjects: boolean;
    IsInternal: boolean;
}

export interface VacationType extends SystemListEntity {
    Color: string;
    IsPaid: boolean;
    IncludeInOvertime: boolean;
    IsDefault: boolean;
    IsUsedByVacations: boolean;
}

export interface Threshold extends SystemListEntity {
    Color: string;
    Value: number;
    IsInternal: boolean;
    NameWithPercent?: string;
}

export interface CustomFields {
    [key: string]: any;
}

export enum AppEntity {
    AllActivities = "AllActivities",
    Task = "Task",
    Issue = "Issue",
    Request = "Request",
    Project = "Project",
    Customer = "Customer",
    User = "User",
    Portfolio = "Portfolio",
    Payment = "Payment",
    TimeEntry = "TimeEntry",
    Expense = "Expense",
}

export enum AppExtEntity {
    JobRole = "JobRole",
    ProjectArchive = "ProjectArchive",
    Milestone = "Milestone",
    OtherProjects = "OtherProjects",
    WithoutPortfolio = "WithoutPortfolio",
    OtherPortfolios = "OtherPortfolios",
    CustomField = "CustomField",
    CustomForm = "CustomForm",
}

export type UnionAppEntity = AppEntity | AppExtEntity;

export const activityTypes = [AppEntity.Task, AppEntity.Issue, AppEntity.Request, AppEntity.AllActivities];

export enum CustomFieldTypes {
    string = "String",
    numeric = "Numeric",
    date = "Date",
    boolean = "Boolean",
    list = "List",
    textAreaRich = "TextAreaRich",
    hyperlink = "Hyperlink",
    textArea = "TextArea",
    multiSelect = "MultiSelect",
    formula = "Formula",
}

export enum CustomFieldNumericTypes {
    decimal = "Decimal",
    integer = "Int",
}

export enum CustomFieldFormulaNames {
    decimal = "Decimal",
}

export type UserSchedule = Partial<server.users.UserScheduleModel>;

export type UserVacation = Partial<server.users.VacationsModel>;

export interface Attachment {
    AttachmentID: number;
    DownloadUrl: string;
    FileName: string;
    Size: number;
}

export interface Preset {
    Id: number;
    Name: string;
    IsMy: boolean;
    UserId: number;
    UserName: string;
    IsDefault: boolean;
    IsShared: boolean;
    IsHidden: boolean;
    IsTemporary: boolean;
    BasePresetId?: number;

    CanDelete: boolean;
    CanSave: boolean;
    CanUnshare: boolean;
    CanRename: boolean;

    CurrentView: keyof typeof AcViewType;

    SpaceId: number;
}

export interface OvertimeThreshold {
    Id: number;
    Threshold: number;
    RateMultiplier: number;
}

export interface PublicHoliday extends SystemListEntity {
    Date: Date;
}

export interface User {
    UserId: number;
    Name: string;
    RoleId?: number;
    IsGuest?: boolean;
    IsEnabled?: boolean;
    Email?: string;
    CustomerId?: number;
    JobRoleId?: number;
    SkillIds?: number[];
    HourlyRateInternal?: number;
    Description?: string;
}

export enum AssigneeTypeEnum {
    User = "User",
    Team = "Team",
    UnnamedResource = "UnnamedResource",
}

export interface UnnamedResource {
    Id?: number;
    UnnamedResourceId?: number;
    Name: string;
    JobRoleId?: number;
    Skills?: number[];
    SkillIds?: number[];
    Status?: ApproverStatus;
}

export type AssignedResource = Partial<UnnamedResource> &
    Partial<User> & {
        ProjectMemberRoleId?: number;
    };

export type AssignedResourceId = Pick<AssignedResource, "UserId" | "UnnamedResourceId">;

export type AssigneeId = Partial<
    // to make properties optional
    Pick<client.members.MemberModel, "UnnamedResourceName" | "Name" | "JobRoleId" | "Skills">
> & {
    UserID?: number;
    UnnamedResourceID?: number;
    RoleID?: number;
};

export interface Notification extends NotificationCommon {
    NotificationId: number;
    Name: string;
    ProjectId: number;
    Enabled: boolean;
    IsSystem?: boolean;
    Recipients: number;
    ExtraRecipients?: number[];
}

export interface NotificationCommon {
    NotifyWhoMadeChange: boolean;
    NotifyCreator: boolean;
    EventIssueAdd: boolean;
    EventIssueEdit: boolean;
    EventIssueAssign: boolean;
    EventIssueCanStart: boolean;
    EventIssueComplete: boolean;
    EventIssueDelete: boolean;
    EventIssueLate: boolean;
    EventTaskAdd: boolean;
    EventTaskEdit: boolean;
    EventTaskAssign: boolean;
    EventTaskCanStart: boolean;
    EventTaskComplete: boolean;
    EventTaskDelete: boolean;
    EventTaskLate: boolean;
    EventRequestAdd: boolean;
    EventRequestEdit: boolean;
    EventRequestAssign: boolean;
    EventRequestCanStart: boolean;
    EventRequestComplete: boolean;
    EventRequestDelete: boolean;
    EventRequestLate: boolean;
    EventMessageAdd: boolean;
    EventExpenseAdd: boolean;
    EventExpenseEdit: boolean;
}

export interface Team {
    TeamId?: number;
    Name?: string;
    Users?: number[];
}

export interface JobRole {
    JobRoleId?: number;
    Name?: string;
}

export type Users = Entities<User>;
export type Teams = Entities<Team>;
export type JobRoles = Entities<JobRole>;
export type UnnamedResources = Entities<UnnamedResource>;

export interface Vacation {
    VacationId: number;
    VacationTypeId: number;
    UserId: number;
    Date: Date;
    IsHalfDay: boolean;
}

export interface VacationTypeColor {
    Id: number;
    Name: string;
    Color: string;
}

export type Vacations = Entities<Vacation>;
export type VacationTypesColors = Entities<VacationTypeColor>;

export interface Status {
    Id: number;
    Name: string;
    Order?: number;
}

export type UserPersonalSchedule = {
    UserId: number;
    MondayHours: number;
    TuesdayHours: number;
    WednesdayHours: number;
    ThursdayHours: number;
    FridayHours: number;
    SaturdayHours: number;
    SundayHours: number;
};

export * from "./customFields";
